
import { useQuery } from "@tanstack/react-query";
import { UserRound, Mail, Phone, FileText } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import type { ClientContact } from "../types";

interface ContactListProps {
  clientId: string;
}

export const ContactList = ({ clientId }: ContactListProps) => {
  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ["client-contacts", clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_contacts")
        .select("*")
        .eq("client_id", clientId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ClientContact[];
    },
  });

  if (isLoading) {
    return <div className="text-center py-4">Loading contacts...</div>;
  }

  if (contacts.length === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        No contacts added yet
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {contacts.map((contact) => (
        <Card key={contact.id}>
          <CardHeader className="flex-row items-center gap-4 space-y-0">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <UserRound className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">{contact.name}</h3>
              <Badge variant="secondary" className="mt-1">
                {contact.role}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {contact.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <a href={`mailto:${contact.email}`} className="hover:underline">
                  {contact.email}
                </a>
              </div>
            )}
            {contact.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <a href={`tel:${contact.phone}`} className="hover:underline">
                  {contact.phone}
                </a>
              </div>
            )}
            {contact.notes && (
              <div className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{contact.notes}</span>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
